<template>
  <div class="free-appointment-flow">
    <AvailableDates
      v-if="appointmentStore.availableDates"
      :question-index="1"
      :component-key="componentKey"
      :loading-dates="appointmentStore.loadingDates"
      :start-date="startDate"
      :disabled="disabled"
      :available-dates="appointmentStore.availableDates"
      :disabled-dates="appointmentStore.disabledDates"
      :selected-date="appointmentStore.selectedDate"
      @monthUpdate="onMonthUpdate"
      @selectDate="onDateSelected"
    />
    <AvailableHours
      v-if="appointmentStore.selectedDate"
      :question-index="2"
      :loading-hours="appointmentStore.loadingHours"
      :available-hours="appointmentStore.selectedDateHours"
      :selected-hour="appointmentStore.selectedHour"
      :disabled="disabled"
      @select-hour="onHourSelected"
    />
    <AvailableAdvisers
      v-if="appointmentStore.selectedDate && appointmentStore.selectedHour"
      :question-index="3"
      :has-prescreening-been-checked="true"
      :available-advisers="appointmentStore.availableAdvisers"
      :selected-adviser="appointmentStore.selectedAdviser"
      :loading-advisers="appointmentStore.loadingAdvisers"
      :disabled="disabled"
      @selectAdviser="onAdviserSelected"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, watch, ref } from 'vue'
import AvailableDates from '~conciergeUiCommons/components/AppointmentQuestionComponents/AvailableDates.vue'
import AvailableHours from '~conciergeUiCommons/components/AppointmentQuestionComponents/AvailableHours.vue'
import AvailableAdvisers from '~conciergeUiCommons/components/AppointmentQuestionComponents/AvailableAdvisers.vue'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { Adviser, Hour, AppointmentDate } from '~concierge/types/AppointmentTypes'
import { getFirstDayOfMonth } from '~conciergeUiCommons/helpers/DateHelper'
import {
  fetchAvailableDatesForAllAdvisers,
  fetchAvailableAdvisersForSpecificHour,
  setAvailableHoursByDate
} from '~concierge/services/AppointmentService'
import { trackUserInteractionStart } from '~conciergeUiCommons/utils/analytics'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'
export default defineComponent({
  name: 'FreeAppointment',
  components: {
    AvailableDates,
    AvailableHours,
    AvailableAdvisers
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(_, { emit }) {
    const appointmentStore = useAppointmentStore()

    const currentDate = new Date()
    onMounted(() => {
      fetchAvailableDatesForAllAdvisers(currentDate.getFullYear(), currentDate.getMonth() + 1)
      watch(appointmentStore.appointment, newValue => {
        const { date, adviser, hour } = newValue

        const isFreeAppointmentFormCompleted = !!date && !!hour && !!adviser

        if (isFreeAppointmentFormCompleted) {
          emit('appointment-selected', newValue)
        }
      })
    })

    const startDate = ref(getFirstDayOfMonth())
    const componentKey = ref(0)

    return {
      componentKey,
      startDate,
      appointmentStore
    }
  },
  emits: ['appointment-selected'],
  methods: {
    onDateSelected(date: AppointmentDate): void {
      this.appointmentStore.resetAppointment()
      this.appointmentStore.resetSelectedHour()
      this.appointmentStore.resetSelectedAdviser()
      this.appointmentStore.selectedDate = date
      setAvailableHoursByDate(date)

      this.appointmentStore.appointment.date = this.appointmentStore.selectedDate
      trackUserInteractionStart(
        ConciergeAppointmentQuestionFlow.FreeAppointment,
        this.appointmentStore.getFormType()
      )
    },
    async onMonthUpdate(update: Record<string, number>): Promise<void> {
      await fetchAvailableDatesForAllAdvisers(update.year, update.month + 1)

      this.startDate = getFirstDayOfMonth(update.year, update.month)
      this.componentKey++
    },
    async onHourSelected(hour: Hour): Promise<void> {
      this.appointmentStore.resetSelectedAdviser()
      this.appointmentStore.appointment.adviser = null
      this.appointmentStore.selectedHour = hour
      this.appointmentStore.appointment.hour = this.appointmentStore.selectedHour
      await fetchAvailableAdvisersForSpecificHour(
        this.appointmentStore.selectedDate,
        this.appointmentStore.selectedHour
      )
    },
    onAdviserSelected(adviser: Adviser): void {
      this.appointmentStore.selectedAdviser = adviser
      this.appointmentStore.appointment.adviser = this.appointmentStore.selectedAdviser
    }
  }
})
</script>
<style scoped lang="scss">
:deep(.section-title) {
  display: inline-flex;
  place-items: center;
  font-size: var(--font-size-large);
  font-weight: 600;
}

:deep(.section-title > div:first-child) {
  margin-right: 0.5em;
}
.mobile {
  .row.g-5 {
    max-width: 100vw;
    margin: 0 auto;
  }
}
</style>
